import { useRouter } from "next/router";
import { Tab } from "@/types/horseRacing";
import { handleize } from "@/utils/horseRacing";

type UrlQueryParams = {
  track: string[];
  race?: number | string;
  tab?: number | string;
};

const DEFAULT_RACE_NUMBER: Tab = 1;
const DEFAULT_TAB_NUMBER: Tab = 1;

const ALLOWED_TAB_VALUES = new Set([1, 2, "1", "2"]);
const isTabValid = (
  tab: Tab | number | string | null | undefined
): tab is Tab | "1" | "2" => {
  return tab != null && ALLOWED_TAB_VALUES.has(tab);
};

function coerceToInt(
  value: string | string[] | undefined,
  defaultValue: number
): number {
  let int = defaultValue;

  // Coerce possible values to an integer
  let str = "";
  if (value != null) {
    if (Array.isArray(value)) {
      str = value[0];
    } else if (typeof value === "string") {
      str = value;
    }

    int = Number.parseInt(str || defaultValue.toString(), 10);

    if (Number.isNaN(int)) {
      int = defaultValue;
    }
  }

  return int;
}

const useHorseRacingRouting = () => {
  const {
    query: { track, race: raceNumber, tab },
  } = useRouter();

  const raceInt = coerceToInt(raceNumber, DEFAULT_RACE_NUMBER);
  const tabInt = coerceToInt(tab, DEFAULT_TAB_NUMBER);

  const [trackCode = "", trackName = ""] = track ?? [];

  /**
   * Any <Link>'s href prop needs to receive an object rather than a string-based URL in order for navigation
   * to work correctly with Next.js's dynamic routing. This function should be used for consistency and to avoid
   * navigation bugs.
   */
  const buildHrefObject = (
    trackCode: string,
    trackName: string,
    raceNumber: number | string | null | undefined,
    tab: Tab | number | string | null | undefined
  ) => {
    const query: UrlQueryParams = {
      track: [trackCode, handleize(trackName)],
    };

    if (raceNumber != null) {
      query.race = raceNumber.toString();
    }

    if (isTabValid(tab)) {
      query.tab = tab.toString();
    }

    return {
      pathname: "/horse-racing/picks/[...track]",
      query,
    };
  };

  /**
   * Builds a string URL STRICTLY for analytics/logging purposes.
   */
  const buildHrefString = (
    trackCode: string,
    trackName: string,
    raceNumber: number | string | null | undefined,
    tab: "1" | "2" | null | undefined
  ) => {
    const url = `/horse-racing/picks/${trackCode}/${handleize(trackName)}`;
    const params = new URLSearchParams();

    if (raceNumber != null) {
      params.append("race", raceNumber.toString());
    }
    if (tab != null) {
      params.append("tab", tab.toString());
    }

    const queryString = params.toString();
    return queryString ? `${url}?${queryString}` : url;
  };

  return {
    trackCode,
    trackName: trackName ?? "unknown-track",
    raceNumber: raceInt,
    tab: tabInt,
    buildHrefObject,
    buildHrefString,
  };
};

export default useHorseRacingRouting;
